import type { ApiEndpointConfigMap, EndpointDef, EndpointHooks, RtkApiLike } from './shared-types';

type RuntimeEndpointConfig = Pick<EndpointDef, 'generated' | 'method'>;
type RuntimeEndpointConfigMap = Record<string, RuntimeEndpointConfig>;

type EndpointHook<TApi extends RtkApiLike, TGenerated extends string, THook extends string> =
  TApi['endpoints'][TGenerated] extends Record<THook, infer TValue> ? TValue : never;

export type HookExports<TConfig extends ApiEndpointConfigMap, TApi extends RtkApiLike> = {
  [K in keyof TConfig as `use${Capitalize<K & string>}`]: TConfig[K]['method'] extends 'get'
    ? EndpointHook<TApi, TConfig[K]['generated'], 'useQuery'>
    : EndpointHook<TApi, TConfig[K]['generated'], 'useMutation'>;
} & {
  [
    K in keyof TConfig as TConfig[K]['method'] extends 'get'
      ? `useLazy${Capitalize<K & string>}`
      : never
  ]: EndpointHook<TApi, TConfig[K]['generated'], 'useLazyQuery'>;
};

const upperFirst = (value: string): string => {
  if (!value) {
    return value;
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const isEndpointHookObject = (value: unknown): value is EndpointHooks => {
  return typeof value === 'object' && value !== null;
};

const assertHookExports = (
  hooks: Record<string, unknown>,
  config: RuntimeEndpointConfigMap,
): void => {
  for (const key in config) {
    const definition = config[key];
    const capitalizedKey = upperFirst(key);
    const hookKey = `use${capitalizedKey}`;

    if (!(hookKey in hooks)) {
      throw new Error(`Missing hook export for endpoint "${key}"`);
    }

    if (definition.method === 'get') {
      const lazyHookKey = `useLazy${capitalizedKey}`;

      if (!(lazyHookKey in hooks)) {
        throw new Error(`Missing lazy hook export for GET endpoint "${key}"`);
      }
    }
  }
};

export function createHookExports<TConfig extends ApiEndpointConfigMap, TApi extends RtkApiLike>(
  config: TConfig,
  api: TApi,
): HookExports<TConfig, TApi>;
export function createHookExports(
  config: RuntimeEndpointConfigMap,
  api: RtkApiLike,
): Record<string, unknown> {
  const hooks: Record<string, unknown> = {};

  for (const key in config) {
    const definition = config[key];
    const generatedEndpointName: string = definition.generated;
    const endpoint = api.endpoints[generatedEndpointName];
    const capitalizedKey = upperFirst(key);

    if (!isEndpointHookObject(endpoint)) {
      continue;
    }

    if (definition.method === 'get') {
      hooks[`use${capitalizedKey}`] = endpoint.useQuery;
      hooks[`useLazy${capitalizedKey}`] = endpoint.useLazyQuery;
      continue;
    }

    hooks[`use${capitalizedKey}`] = endpoint.useMutation;
  }

  assertHookExports(hooks, config);
  return hooks;
}
